import { createPortal } from "react-dom";

// Shared modal shell for every admin form dialog. Fixes, in one place, the issues that
// were previously copy-pasted into ~10 admin pages: modals opening above the viewport,
// no max-height (so tall forms ran off-screen), and no sticky header/footer while
// scrolling. Rendered via a Portal directly into document.body so it's never trapped
// inside a transformed ancestor's containing block — every admin page root carries a
// persistent `transform` (Framer Motion's animate={{y:0}} or the .fade-up keyframe's
// `to { transform: translateY(0) }`, kept alive by animation-fill-mode: both), and per
// the CSS spec any non-`none` transform on an ancestor creates a new containing block
// for position:fixed descendants — silently confining "fixed" modals to the page
// content box instead of the real viewport (backdrop doesn't cover the sidebar/header,
// modal can scroll away instead of staying pinned). Portal sidesteps this entirely by
// making the modal a direct child of <body>, matching how every Radix/shadcn primitive
// already used in this app (Dialog, Sheet, Drawer, Select, Popover) handles this.
export function AdminModal({ onClose, title, subtitle, footer, children, maxWidth = "max-w-lg", testId }) {
  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 sm:p-6 overflow-y-auto"
      onClick={onClose}
      data-testid={testId}
    >
      <div
        className={`bg-white w-full ${maxWidth} rounded-2xl flex flex-col my-4 sm:my-0 shadow-2xl`}
        style={{ maxHeight: "calc(100vh - 32px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="shrink-0 flex items-start justify-between gap-3 px-6 py-4 border-b border-gray-100">
            <div className="min-w-0">
              <h2 className="rebrand-serif text-lg font-semibold text-[#1A202C] flex items-center gap-2 flex-wrap">{title}</h2>
              {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
            </div>
            <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-full hover:bg-gray-100 shrink-0" aria-label="Close">✕</button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
        {footer && <div className="shrink-0 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-6 py-4 border-t border-gray-100">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}
